import { action, computed, observable, runInAction } from 'mobx';
import {
  DataFieldWithDataType,
  toAdvancedDimension,
  toDataFieldWithDataType,
} from '../../../common-types';
import i18n from '../../../i18n';
import { applicationToOfferTableJoin } from '../../pages/dashboard/recruitment/services/utils';
import {
  ApiHierarchyItem,
  toApiMasterDataAdvancedQueryWithTypeInfo,
} from '../api/api-interfaces';
import {
  TabConfig,
  TabConfigType,
} from '../components/dashboard/dashboard-data-view';
import {
  ApplicationDataFields,
  DataTypes,
  EmploymentTemporality,
  InterviewDataFields,
  JobDataFields,
  OfferDataFields,
  Operations,
  RecruiterJobsDataFields,
} from '../constants/constants';
import {
  getVersionFilter,
  getVersionFilterItems,
} from '../filter/common-filters';
import { TimeSliderConfig } from '../filter/filter-store';
import { combineFilters } from '../filter/utils';
import { FrontendMetricGroupId } from '../metrics/backendMetricMappings';
import { GetUserVisibleNonConfidentialNonNullFieldsForDataTypeService } from '../services/GetNonNullFieldsForDataTypeService';
import { Ordering } from '../utilFunctions/comparators';
import { ExecutorRoleViewAuthorizationService } from '../v2/api/authorization/service';
import { Hasher } from '../v2/api/hasher';
import { HasDependencies, hashString } from '../v2/api/types';
import { AllUsedFields, ApiMasterDataQueryFilterItem } from '../v2/types';
import { ApiMasterDataAdvancedQuery } from './../api/api-interfaces';
import { Ord } from './../utilFunctions/comparators';

export enum RecruitmentStages {
  APPLICATIONS_AT_APPLIED_STAGE = 'APPLICATIONS_AT_APPLIED_STAGE',
  APPLICATIONS_AT_APPLIED_STAGE_PASSED = 'APPLICATIONS_AT_APPLIED_STAGE_PASSED',
  APPLICATIONS_AT_APPLIED_STAGE_REJECTED = 'APPLICATIONS_AT_APPLIED_STAGE_REJECTED',
  APPLICATIONS_AT_APPLIED_STAGE_WITHDRAWN = 'APPLICATIONS_AT_APPLIED_STAGE_WITHDRAWN',
  APPLICATIONS_AT_SCREENING_STAGE = 'APPLICATIONS_AT_SCREENING_STAGE',
  APPLICATIONS_AT_SCREENING_STAGE_PASSED = 'APPLICATIONS_AT_SCREENING_STAGE_PASSED',
  APPLICATIONS_AT_SCREENING_STAGE_REJECTED = 'APPLICATIONS_AT_SCREENING_STAGE_REJECTED',
  APPLICATIONS_AT_SCREENING_STAGE_WITHDRAWN = 'APPLICATIONS_AT_SCREENING_STAGE_WITHDRAWN',
  APPLICATIONS_AT_1ST_INTERVIEW_STAGE = 'APPLICATIONS_AT_1ST_INTERVIEW_STAGE',
  APPLICATIONS_AT_1ST_INTERVIEW_STAGE_PASSED = 'APPLICATIONS_AT_1ST_INTERVIEW_STAGE_PASSED',
  APPLICATIONS_AT_1ST_INTERVIEW_STAGE_REJECTED = 'APPLICATIONS_AT_1ST_INTERVIEW_STAGE_REJECTED',
  APPLICATIONS_AT_1ST_INTERVIEW_STAGE_WITHDRAWN = 'APPLICATIONS_AT_1ST_INTERVIEW_STAGE_WITHDRAWN',
  APPLICATIONS_AT_2ND_INTERVIEW_STAGE = 'APPLICATIONS_AT_2ND_INTERVIEW_STAGE',
  APPLICATIONS_AT_2ND_INTERVIEW_STAGE_PASSED = 'APPLICATIONS_AT_2ND_INTERVIEW_STAGE_PASSED',
  APPLICATIONS_AT_2ND_INTERVIEW_STAGE_REJECTED = 'APPLICATIONS_AT_2ND_INTERVIEW_STAGE_REJECTED',
  APPLICATIONS_AT_2ND_INTERVIEW_STAGE_WITHDRAWN = 'APPLICATIONS_AT_2ND_INTERVIEW_STAGE_WITHDRAWN',
  APPLICATIONS_AT_3RD_INTERVIEW_STAGE = 'APPLICATIONS_AT_3RD_INTERVIEW_STAGE',
  APPLICATIONS_AT_3RD_INTERVIEW_STAGE_PASSED = 'APPLICATIONS_AT_3RD_INTERVIEW_STAGE_PASSED',
  APPLICATIONS_AT_3RD_INTERVIEW_STAGE_REJECTED = 'APPLICATIONS_AT_3RD_INTERVIEW_STAGE_REJECTED',
  APPLICATIONS_AT_3RD_INTERVIEW_STAGE_WITHDRAWN = 'APPLICATIONS_AT_3RD_INTERVIEW_STAGE_WITHDRAWN',
  APPLICATIONS_AT_OFFERED_STAGE = 'APPLICATIONS_AT_OFFERED_STAGE',
  APPLICATIONS_AT_OFFERED_STAGE_PASSED = 'APPLICATIONS_AT_OFFERED_STAGE_PASSED',
  APPLICATIONS_AT_OFFERED_STAGE_REJECTED = 'APPLICATIONS_AT_OFFERED_STAGE_REJECTED',
  APPLICATIONS_AT_OFFERED_STAGE_WITHDRAWN = 'APPLICATIONS_AT_OFFERED_STAGE_WITHDRAWN',
  APPLICATIONS_AT_JOINED_STAGE = 'APPLICATIONS_AT_JOINED_STAGE',
}

// This kind of mapping will be updated and more centralized when we start using
// other metric groups on the frontend, not just recruitment
// We'll probably have a concept of frontend_Metric_id or frontend_metric_group then
export const recruitmentStageToFrontendMetricGroupIdMap: Record<
  RecruitmentStages,
  FrontendMetricGroupId
> = {
  [RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_APPLIED_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_APPLIED_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_APPLIED_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_APPLIED_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_SCREENING_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_SCREENING_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_SCREENING_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_SCREENING_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_1ST_INTERVIEW_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_2ND_INTERVIEW_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_3RD_INTERVIEW_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_OFFERED_STAGE,
  [RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_PASSED]:
    FrontendMetricGroupId.APPLICATIONS_AT_OFFERED_STAGE_PASSED,
  [RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_REJECTED]:
    FrontendMetricGroupId.APPLICATIONS_AT_OFFERED_STAGE_REJECTED,
  [RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_WITHDRAWN]:
    FrontendMetricGroupId.APPLICATIONS_AT_OFFERED_STAGE_WITHDRAWN,
  [RecruitmentStages.APPLICATIONS_AT_JOINED_STAGE]:
    FrontendMetricGroupId.APPLICATIONS_AT_JOINED_STAGE,
};

export const recruitmentFrontendMetricGroups: FrontendMetricGroupId[] = [
  RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE,
  RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_APPLIED_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE,
  RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_SCREENING_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE,
  RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_1ST_INTERVIEW_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE,
  RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_2ND_INTERVIEW_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE,
  RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_3RD_INTERVIEW_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE,
  RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_PASSED,
  RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_REJECTED,
  RecruitmentStages.APPLICATIONS_AT_OFFERED_STAGE_WITHDRAWN,
  RecruitmentStages.APPLICATIONS_AT_JOINED_STAGE,
].map((s) => recruitmentStageToFrontendMetricGroupIdMap[s]);

export enum ApplicationStages {
  APPLIED = 'Applied',
  SCREENING = 'Screening',
  FIRST_INTERVIEW_PASSED = '1st Interview Passed',
  SECOND_INTERVIEW_PASSED = '2nd Interview Passed',
  THIRD_INTERVIEW_PASSED = '3rd Interview Passed',
  LAST_INTERVIEW_PASSED = 'Last Interview Passed',
  OFFERED = 'Offered',
  ACCEPTED = 'Accepted',
  JOINED = 'Joined',
}

export enum RequisitionActivities {
  APPLICATION = 'Application',
  SCREENING = 'Screening',
  INTERVIEW = 'Interview',
  OFFER = 'Offer',
  HIRE = 'Hire',
}

// tslint:disable-next-line: no-namespace
export namespace ApplicationStages {
  export const orderedList: ApplicationStages[] = [
    ApplicationStages.APPLIED,
    ApplicationStages.SCREENING,
    ApplicationStages.FIRST_INTERVIEW_PASSED,
    ApplicationStages.SECOND_INTERVIEW_PASSED,
    ApplicationStages.THIRD_INTERVIEW_PASSED,
    ApplicationStages.LAST_INTERVIEW_PASSED,
    ApplicationStages.OFFERED,
    ApplicationStages.ACCEPTED,
    ApplicationStages.JOINED,
  ];
  export const sortOrder: Ord<ApplicationStages> = {
    compare: (s1: ApplicationStages, s2: ApplicationStages): Ordering => {
      const s1Index = orderedList.findIndex((s) => s === s1);
      const s2Index = orderedList.findIndex((s) => s === s2);
      if (s1Index && s2Index) {
        return s1Index < s2Index
          ? Ordering.LT
          : s1Index > s2Index
            ? Ordering.GT
            : Ordering.EQ;
      } else {
        return Ordering.EQ;
      }
    },
  };

  export const translationKeys: Record<ApplicationStages, string> = {
    [ApplicationStages.APPLIED]: 'common:filterTray.stages.applied',
    [ApplicationStages.SCREENING]: 'common:filterTray.stages.screening',
    [ApplicationStages.FIRST_INTERVIEW_PASSED]:
      'common:filterTray.stages.firstInterviewPassed',
    [ApplicationStages.SECOND_INTERVIEW_PASSED]:
      'common:filterTray.stages.secondInterviewPassed',
    [ApplicationStages.THIRD_INTERVIEW_PASSED]:
      'common:filterTray.stages.thirdInterviewPassed',
    [ApplicationStages.LAST_INTERVIEW_PASSED]:
      'common:filterTray.stages.lastInterviewPassed',
    [ApplicationStages.OFFERED]: 'common:filterTray.stages.offered',
    [ApplicationStages.ACCEPTED]: 'common:filterTray.stages.accepted',
    [ApplicationStages.JOINED]: 'common:filterTray.stages.joined',
  };

  export const getTranslation = (stage: ApplicationStages) =>
    i18n.t(ApplicationStages.translationKeys[stage]);
}

export class RecruitmentStore implements HasDependencies {
  constructor(
    readonly authorizationService: ExecutorRoleViewAuthorizationService,
    readonly allUsedFields: AllUsedFields
  ) {}

  public getDeps = () => [
    hashString([
      ...this.authorizationService.getDeps(),
      Hasher.hash(this.allUsedFields),
    ]),
  ];

  @computed
  public get RecruitmentDataTypes(): DataTypes[] {
    // Adding survey types for backward compatibility but they might not be needed here
    return [
      DataTypes.APPLICATION,
      DataTypes.OFFER,
      DataTypes.RECRUITERJOBS,
      DataTypes.JOB,
      DataTypes.QUESTIONANSWER,
    ];
  }

  public isRecruitmentDataType = (dataType: DataTypes) => {
    return this.RecruitmentDataTypes.includes(dataType);
  };

  @observable
  public sources: ApiHierarchyItem[] = [];

  @observable
  public commonLegendsHashmap: any = {};

  @observable
  public commonLegendLabels: string[] = [];

  @observable
  public recruiterJobMap: any = {};

  @observable
  public recruiterActivityDimension = 'OFFICE';

  @observable
  public recruitmentDataViewTabConfig: TabConfig[] = [];

  @observable
  public enabledRecruitmentActivities: string[] = Object.values(
    RequisitionActivities
  );

  public proactiveSources = [
    'In person event',
    'Prospecting',
    'Agency',
    'Sourcing',
  ];

  public get hierarchichalSources(): ApiHierarchyItem[] {
    const result = this.sources.reduce(
      (result, source) => {
        if (this.proactiveSources.includes(source.name)) {
          result[1].subItems?.push(source);
        } else {
          result[0].subItems?.push(source);
        }
        return result;
      },
      [
        { name: 'Inbound', subItems: [] },
        { name: 'Outbound', subItems: [] },
      ] as ApiHierarchyItem[]
    );
    return result;
  }

  @action
  public getDataViewTabConfigs = (
    filters: ApiMasterDataQueryFilterItem[],
    latestVersions: Partial<Record<DataTypes, string>>,
    timeSliderConfig: TimeSliderConfig
  ) => {
    const { startDate, endDate } = timeSliderConfig;

    const dataViewTabs: DataTypes[] = [
      DataTypes.JOB,
      DataTypes.RECRUITERJOBS,
      DataTypes.APPLICATION,
      DataTypes.OFFER,
      DataTypes.INTERVIEW,
    ];

    const config: TabConfig[] = [];
    const dataTypeToDataFieldsMap: Partial<
      Record<DataTypes, DataFieldWithDataType[]>
    > = dataViewTabs.reduce<
      Partial<Record<DataTypes, DataFieldWithDataType[]>>
    >((acc, t) => {
      return {
        ...acc,
        [t]: GetUserVisibleNonConfidentialNonNullFieldsForDataTypeService(
          t,
          this.authorizationService,
          this.allUsedFields
        ),
      };
    }, {});
    for (const dataType of dataViewTabs) {
      const latestVersion = latestVersions[dataType];
      const dataFields = dataTypeToDataFieldsMap[dataType] ?? [];
      if (dataFields.length && latestVersion) {
        const query: ApiMasterDataAdvancedQuery = {
          dataType,
          dimensions: dataFields.map((field) => toAdvancedDimension(field)),
          filterItems: [
            ...combineFilters(filters.filter((f) => f.dataType === dataType)),
            ...this.getTimeFiltersForDataView(
              dataType,
              startDate,
              endDate,
              latestVersion
            ),
          ],
        };
        if (dataType === DataTypes.OFFER) {
          const candidateNameFields = [
            {
              dataType: DataTypes.APPLICATION,
              property: ApplicationDataFields.CANDIDATE_FIRST_NAME,
            },
            {
              dataType: DataTypes.APPLICATION,
              property: ApplicationDataFields.CANDIDATE_LAST_NAME,
            },
          ].filter((f) =>
            dataTypeToDataFieldsMap[DataTypes.APPLICATION]?.deepCompareContains(
              toDataFieldWithDataType(f.dataType, f.property)
            )
          );
          if (candidateNameFields.length) {
            query.dimensions?.splice(1, 0, ...candidateNameFields);
            query.joins = [applicationToOfferTableJoin];
          }
        }

        config.push({
          type: TabConfigType.TabConfigWithQueries,
          title: `pages:dashboards.recruitment.dataView.${dataType.toCamel().replace('_', '')}`,
          value: {
            queriesWithType: [toApiMasterDataAdvancedQueryWithTypeInfo(query)],
          },
        });
      }
    }

    this.recruitmentDataViewTabConfig = config;
  };

  @action
  public handleRecruiterActivityDimension = (
    _event: React.ChangeEvent<{}>,
    value: string
  ) => {
    this.recruiterActivityDimension = value;
  };

  public setRecruiterActivityDimension = (dimension: string) => {
    runInAction(() => {
      this.recruiterActivityDimension = dimension;
    });
  };

  @action
  public toggleCommonLegend(key: string) {
    if (key in this.commonLegendsHashmap) {
      this.commonLegendsHashmap[key] = !this.commonLegendsHashmap[key];
    }
  }

  @action
  public setCommonLegendHashmap(labels: string[]) {
    this.commonLegendLabels = labels;
    this.commonLegendsHashmap = labels.reduce((result: any, label) => {
      result[label] = true;
      return result;
    }, {});
  }

  private getTimeFiltersForDataView = (
    dataType: DataTypes,
    startDate: string,
    endDate: string,
    latestVersion: string
  ): ApiMasterDataQueryFilterItem[] => {
    if (latestVersion) {
      if (dataType === DataTypes.JOB) {
        return [
          {
            operation: Operations.GREATER_THAN_OR_EQUAL_TO,
            property: JobDataFields.OPENED_AT,
            values: [startDate],
            dataType,
          },
          {
            operation: Operations.LESS_THAN_OR_EQUAL_TO,
            property: JobDataFields.OPENED_AT,
            values: [endDate],
            dataType,
          },
          getVersionFilter(latestVersion, Operations.EQUAL, dataType),
        ];
      } else if (dataType === DataTypes.EMPLOYEE) {
        return getVersionFilterItems(endDate);
      } else if (dataType === DataTypes.RECRUITERJOBS) {
        return [
          {
            property: RecruiterJobsDataFields.RECRUITER_TEMPORALITY,
            operation: Operations.EQUAL,
            values: [EmploymentTemporality.PRESENT, EmploymentTemporality.PAST],
            dataType,
          },
          getVersionFilter(latestVersion, Operations.EQUAL, dataType),
        ];
      } else if (dataType === DataTypes.APPLICATION) {
        return [
          {
            operation: Operations.GREATER_THAN_OR_EQUAL_TO,
            property: ApplicationDataFields.APPLIED_AT,
            values: [startDate],
            dataType,
          },
          {
            operation: Operations.LESS_THAN_OR_EQUAL_TO,
            property: ApplicationDataFields.APPLIED_AT,
            values: [endDate],
            dataType,
          },
          getVersionFilter(latestVersion, Operations.EQUAL, dataType),
        ];
      } else if (dataType === DataTypes.OFFER) {
        return [
          {
            operation: Operations.GREATER_THAN_OR_EQUAL_TO,
            property: OfferDataFields.RESOLVED_AT,
            values: [startDate],
            dataType,
          },
          {
            operation: Operations.LESS_THAN_OR_EQUAL_TO,
            property: OfferDataFields.RESOLVED_AT,
            values: [endDate],
            dataType,
          },
          getVersionFilter(latestVersion, Operations.EQUAL, dataType),
        ];
      } else if (dataType === DataTypes.INTERVIEW) {
        return [
          {
            operation: Operations.GREATER_THAN_OR_EQUAL_TO,
            property: InterviewDataFields.EFFECTIVE_DATE,
            values: [startDate],
            dataType,
          },
          {
            operation: Operations.LESS_THAN_OR_EQUAL_TO,
            property: InterviewDataFields.EFFECTIVE_DATE,
            values: [endDate],
            dataType,
          },
        ];
      }
    }
    return [];
  };
}
